import React from 'react';
import type { InventoryItem } from '../../types/inventory';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ForbiddenState } from '../../components/common/ForbiddenState';
import { RoastedBulkGroups } from '../../components/inventory/RoastedBulkGroups';
import { InventoryFilters } from './components/InventoryFilters';
import { InventoryHeader } from './components/InventoryHeader';
import { InventoryModals } from './components/InventoryModals';
import { InventoryTable } from './components/InventoryTable';
import { InventoryTypeSelector } from './components/InventoryTypeFilter';
import { useInventoryPageState } from '../../hooks/inventory-hooks/useInventoryPageState';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';

export const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('inventory', 'write');
  const canDelete = can('inventory', 'delete');
  const {
    currencySymbol,
    items,
    loading,
    error,
    isForbidden,
    typeFilter,
    setTypeFilter,
    nameFilter,
    setNameFilter,
    statusFilter,
    setStatusFilter,
    stockStatusFilter,
    setStockStatusFilter,
    paginatedItems,
    totalFiltered,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortConfig,
    handleSort,
    expandedRowIds,
    toggleRow,
    editingItem,
    setEditingItem,
    historyItemId,
    setHistoryItemId,
    isCreateModalOpen,
    setIsCreateModalOpen,
    isFinishedGoodsModalOpen,
    setIsFinishedGoodsModalOpen,
    isImportModalOpen,
    setIsImportModalOpen,
    isImportRBModalOpen,
    setIsImportRBModalOpen,
    roastProfileContext,
    setRoastProfileContext,
    tasteProfileContext,
    setTasteProfileContext,
    tasteProfileListItem,
    setTasteProfileListItem,
    handleRestock,
    handleEditItem,
    handleDelete,
    formatDate,
    handleInventoryRefresh,
  } = useInventoryPageState();

  if (isForbidden) {
    return <ForbiddenState variant="page" />;
  }

  if (loading && items.length === 0) {
    return <LoadingSpinner text="Loading inventory..." />;
  }

  const handleAddItem = () => {
    if (typeFilter === 'finished_good') {
      navigate('/work-orders?mode=finished_good');
    } else {
      setIsCreateModalOpen(true);
    }
  };

  const handleRowClick = (item: InventoryItem) => {
    setEditingItem(item);
    setIsCreateModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <InventoryHeader
        typeFilter={typeFilter}
        onAddItem={handleAddItem}
        onImportGreenBean={() => setIsImportModalOpen(true)}
        onImportRoastedBulk={() => setIsImportRBModalOpen(true)}
        canWrite={canWrite}
      />

      <InventoryTypeSelector typeFilter={typeFilter} onChange={setTypeFilter} />
      
      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <InventoryFilters
        nameFilter={nameFilter}
        onNameChange={setNameFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        stockStatusFilter={stockStatusFilter}
        onStockStatusChange={setStockStatusFilter}
      />
      
      <InventoryModals
        items={items}
        historyItemId={historyItemId}
        onCloseHistory={() => setHistoryItemId(null)}
        onHistoryUpdated={handleInventoryRefresh}
        editingItem={editingItem}
        typeFilter={typeFilter}
        isCreateModalOpen={isCreateModalOpen}
        onCloseCreateModal={() => {
          setIsCreateModalOpen(false);
          setEditingItem(undefined);
        }}
        onCreateSuccess={async () => {
          await handleInventoryRefresh();
          setIsCreateModalOpen(false);
          setEditingItem(undefined);
        }}
        tasteProfileContext={tasteProfileContext}
        onCloseTasteProfile={() => setTasteProfileContext(null)}
        onTasteProfileSuccess={async () => {
          setTasteProfileContext(null);
          await handleInventoryRefresh();
        }}
        tasteProfileListItem={tasteProfileListItem}
        onCloseTasteProfileList={() => setTasteProfileListItem(null)}
        onOpenTasteProfileList={(item) => {
          setTasteProfileContext(null);
          setTasteProfileListItem(item);
        }}
        onSelectTasteProfile={(item, profileId) => {
          setTasteProfileListItem(null);
          setTasteProfileContext({ item, mode: 'view', existingProfileId: profileId });
        }}
        roastProfileContext={roastProfileContext}
        onCloseRoastProfile={() => setRoastProfileContext(null)}
        onRoastProfileSaved={handleInventoryRefresh}
        isFinishedGoodsModalOpen={isFinishedGoodsModalOpen}
        onCloseFinishedGoodsModal={() => setIsFinishedGoodsModalOpen(false)}
        onFinishedGoodsSuccess={async () => {
          await handleInventoryRefresh();
          setIsFinishedGoodsModalOpen(false);
        }}
        isImportModalOpen={isImportModalOpen}
        onCloseImportModal={() => setIsImportModalOpen(false)}
        onImportSuccess={async () => {
          await handleInventoryRefresh();
          setIsImportModalOpen(false);
        }}
        isImportRBModalOpen={isImportRBModalOpen}
        onCloseImportRBModal={() => setIsImportRBModalOpen(false)}
        onImportRBSuccess={async () => {
          await handleInventoryRefresh();
          setIsImportRBModalOpen(false);
        }}
        canWrite={canWrite}
      />

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {typeFilter === 'roasted_bulk' ? (
          <RoastedBulkGroups
            nameFilter={nameFilter}
            statusFilter={statusFilter}
            stockStatusFilter={stockStatusFilter}
            currencySymbol={currencySymbol}
            items={items}
            onEditItem={handleEditItem}
            onOpenHistory={(itemId) => setHistoryItemId(itemId)}
            onOpenRoastProfile={(context) => setRoastProfileContext(context)}
            canWrite={canWrite}
          />
        ) : (
          <>
            <InventoryTable
              typeFilter={typeFilter}
              items={paginatedItems}
              sortConfig={sortConfig}
              onSort={handleSort}
              expandedRowIds={expandedRowIds}
              onToggleRow={toggleRow}
              currencySymbol={currencySymbol}
              onRowClick={handleRowClick}
              onEditItem={(item) => handleEditItem(item.id)}
            canWrite={canWrite}
              canDelete={canDelete}
              onOpenHistory={(item) => setHistoryItemId(item.id)}
              onOpenRoastProfile={setRoastProfileContext}
              onOpenTasteProfile={setTasteProfileContext}
              onRestock={handleRestock}
              onDeleteItem={(item) => handleDelete(item.id)}
              formatDate={formatDate}
              onProfileUpdated={handleInventoryRefresh}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalFiltered}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>
    </div>
  );
};
